import { useContext, useEffect, useState, useRef, useCallback } from 'react';
import axios from 'axios';
import { 
  Box, 
  Typography, 
  Button, 
  Paper,
  Stack,
  Snackbar,
  Alert
} from '@mui/material';
import LockIcon from '@mui/icons-material/Lock';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import DescriptionIcon from '@mui/icons-material/Description';
import ErrorIcon from '@mui/icons-material/Error';
import { ReportContext } from '../Context';
import { UserContext } from '../Context';
import { SocketContext } from '../Context';

import { io } from 'socket.io-client';
import Up from './Up';
import Left from './Left';
import Central from './Central';
import Right from './Right';
import SettingsFieldDisplay from '../tool/SettingsFieldDisplay';
import { logAction, formatReportNumber, LOG_ACTIONS } from '../../utils/logClient';

function Core() {
  const { report, pickReport } = useContext(ReportContext);
  const { user } = useContext(UserContext);
  const { socket, setSocket } = useContext(SocketContext);
  const [reportData, setReportData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isModerator, setIsModerator] = useState(false);
  const [connectedUsers, setConnectedUsers] = useState([]);
  const [totalConnectedUsers, setTotalConnectedUsers] = useState(0);
  const [presenceNotifications, setPresenceNotifications] = useState([]);
  const [accessDenied, setAccessDenied] = useState(false);
  const [accessDeniedMessage, setAccessDeniedMessage] = useState('');
  const [userNamesCache, setUserNamesCache] = useState({});
  const [leftWidth, setLeftWidth] = useState(23);
  const [rightWidth, setRightWidth] = useState(23);
  const [isDragging, setIsDragging] = useState(null); // 'left', 'right', or null
  const [dragStartX, setDragStartX] = useState(0);
  const [initialLeftWidth, setInitialLeftWidth] = useState(23);
  const accessUpdateRef = useRef({});
  const userNamesCacheRef = useRef({});
  const hasInitializedUsersRef = useRef(false);
  const hasLoggedEntryRef = useRef(false);
  const currentReportRef = useRef(null);
  const socketRef = useRef(null);
  const isConnectingRef = useRef(false);

  const isRestricted = reportData?.access?.type === 'restricted';
  const userHasAccess = isRestricted
    ? Array.isArray(reportData?.access?.users) &&
      (reportData.access.users.includes(user) || isModerator)
    : true;

  const fetchUserDisplayName = useCallback(async (matricule) => {
    if (!matricule || userNamesCacheRef.current[matricule]) {
      return userNamesCacheRef.current[matricule];
    }

    try {
      const token = sessionStorage.getItem('authToken');
      const response = await axios.get(`/api/user/avatar/${matricule}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const { rank, lastname, firstname } = response.data || {};
      const displayName = [
        rank ? rank.toUpperCase() : '',
        lastname ? lastname.toUpperCase() : '',
        firstname || ''
      ]
        .filter(Boolean)
        .join(' ')
        .trim() || matricule;

      userNamesCacheRef.current[matricule] = displayName;
      setUserNamesCache((prev) => ({ ...prev, [matricule]: displayName }));
      return displayName;
    } catch (err) {
      console.error('Erreur lors de la récupération du nom utilisateur:', err);
      userNamesCacheRef.current[matricule] = matricule;
      setUserNamesCache((prev) => ({ ...prev, [matricule]: matricule }));
      return matricule;
    }
  }, []);

  const resolveUserName = useCallback(
    (matricule) => userNamesCache[matricule] || matricule,
    [userNamesCache]
  );

  const enqueuePresenceNotification = useCallback((userId, action) => {
    if (!userId) return;
    setPresenceNotifications((prev) => [
      ...prev,
      { id: `${Date.now()}-${Math.random()}`, userId, action }
    ]);
  }, []);

  const handleClosePresenceNotification = useCallback((id) => {
    setPresenceNotifications((prev) => prev.filter((notif) => notif.id !== id));
  }, []);

  // Ajout de l'effet pour connecter au WebSocket
  useEffect(() => {
    // Vérifications préalables
    if (!report || !user) {
      console.log('WebSocket: Pas de report ou user, skipping connection');
      return;
    }

    if (accessDenied) {
      console.log('WebSocket: accès déjà refusé, pas de connexion socket');
      return;
    }

    // Attendre d'avoir les données du PV et vérifier l'accès avant de se connecter
    if (!reportData) return;
    if (isRestricted && !userHasAccess) {
      console.log('WebSocket: accès refusé, pas de connexion socket');
      return;
    }

    const reportId = `${report.number}-${report.year}`;

    // Éviter les connexions multiples au même report
    // Vérifier si on est déjà connecté OU en cours de connexion au même report
    if (currentReportRef.current === reportId) {
      if (socketRef.current || isConnectingRef.current) {
        console.log('WebSocket: déjà connecté ou en cours de connexion au même report, skipping');
        return;
      }
    }

    // Si on change de report, déconnecter l'ancienne socket proprement
    if (socketRef.current && currentReportRef.current && currentReportRef.current !== reportId) {
      console.log(`WebSocket: changement de report ${currentReportRef.current} -> ${reportId}`);
      const oldSocket = socketRef.current;
      const oldReportId = currentReportRef.current;
      
      // Nettoyer l'ancienne connexion
      oldSocket.emit('leaveReport', { reportId: oldReportId });
      oldSocket.disconnect();
      socketRef.current = null;
      isConnectingRef.current = false;
      
      // Log de sortie pour l'ancien report
      if (hasLoggedEntryRef.current) {
        hasLoggedEntryRef.current = false;
        logAction(LOG_ACTIONS.REPORT_LEAVE, formatReportNumber(
          parseInt(oldReportId.split('-')[0]), 
          parseInt(oldReportId.split('-')[1])
        ), {
          indice: reportData?.indice || null
        });
      }
    }

    // Marquer qu'on est en cours de connexion
    isConnectingRef.current = true;
    currentReportRef.current = reportId;

    // Réinitialiser les notifications et le cache lors d'un changement de PV
    setPresenceNotifications([]);
    setUserNamesCache({});
    userNamesCacheRef.current = {};
    hasInitializedUsersRef.current = false;

    // En production, le frontend est servi par le backend derrière un reverse proxy
    // (Nginx/IIS) : on se connecte sur la MÊME origine, Socket.IO passant alors par
    // /socket.io/ sur le port public (443 en HTTPS). Le port 3000 n'y est pas joignable.
    // En développement, le React dev server (port 3001) est distinct du backend (3000)
    // et ne proxifie que /api, il faut donc viser explicitement le port 3000.
    // La garde sur le protocole empêche de reconstruire une URL http://…:3000 depuis une
    // page HTTPS, ce qui serait de toute façon bloqué en contenu mixte.
    const useDevBackendPort =
      process.env.NODE_ENV === 'development' && window.location.protocol !== 'https:';
    const socketUrl = useDevBackendPort
      ? `http://${window.location.hostname}:3000`
      : window.location.origin;
    const token = sessionStorage.getItem('authToken');
    console.log('WebSocket: Tentative de connexion à', socketUrl, 'pour report', reportId);
    
    const newSocket = io(socketUrl, {
      auth: token ? { token } : undefined
    });

    // Événements de connexion
    newSocket.on('connect', () => {
      console.log(`✅ WebSocket connectée (ID: ${newSocket.id}), rejoignant le PV ${reportId} par ${user}`);
      
      // Vérifier que c'est toujours le bon report
      if (currentReportRef.current !== reportId) {
        console.log('WebSocket: report a changé pendant la connexion, déconnexion');
        newSocket.disconnect();
        return;
      }
      
      socketRef.current = newSocket;
      isConnectingRef.current = false;
      setSocket(newSocket);
      
      newSocket.emit('joinReport', { reportId });
      
      // Log de l'entrée dans le rapport (une seule fois)
      if (!hasLoggedEntryRef.current) {
        hasLoggedEntryRef.current = true;
        logAction(LOG_ACTIONS.REPORT_ENTER, formatReportNumber(report.number, report.year), {
          indice: reportData?.indice || null
        });
      }
    });

    newSocket.on('connect_error', (error) => {
      console.error('❌ Erreur de connexion WebSocket:', error);
      isConnectingRef.current = false;
    });

    newSocket.on('disconnect', (reason) => {
      console.log(`🔌 WebSocket déconnectée: ${reason}`);
    });

    // Écouter les mises à jour des utilisateurs connectés
    const handleReportUsersUpdate = (data) => {
      if (data.reportId !== reportId) return;
      console.log(`👥 Utilisateurs connectés mis à jour pour ${reportId}:`, data.connectedUsers);

      setConnectedUsers((previousUsers) => {
        const previousSet = new Set(previousUsers);
        const nextUsers = Array.isArray(data.connectedUsers) ? data.connectedUsers : [];
        const nextSet = new Set(nextUsers);

        if (hasInitializedUsersRef.current) {
          const joined = nextUsers.filter(
            (userId) => !previousSet.has(userId) && userId !== user
          );
          const left = previousUsers.filter(
            (userId) => !nextSet.has(userId) && userId !== user
          );

          joined.forEach((userId) => {
            fetchUserDisplayName(userId);
            enqueuePresenceNotification(userId, 'join');
          });

          left.forEach((userId) => {
            fetchUserDisplayName(userId);
            enqueuePresenceNotification(userId, 'leave');
          });
        } else {
          nextUsers.forEach((userId) => fetchUserDisplayName(userId));
          hasInitializedUsersRef.current = true;
        }

        return nextUsers;
      });

      setTotalConnectedUsers(data.totalUsers);
    };

    newSocket.on('reportUsersUpdate', handleReportUsersUpdate);

    const handleSocketError = (payload) => {
      const message = payload?.message || payload;
      if (
        typeof message === 'string' &&
        (message.toLowerCase().includes('accès refusé') ||
          message.toLowerCase().includes('access denied') ||
          message.toLowerCase().includes('unauthorized'))
      ) {
        setAccessDenied(true);
        setAccessDeniedMessage(message);
        newSocket.emit('leaveReport', { reportId });
        newSocket.disconnect();
        socketRef.current = null;
        isConnectingRef.current = false;
        setSocket(null);
        setConnectedUsers([]);
        setTotalConnectedUsers(0);
      }
    };

    newSocket.on('error', handleSocketError);

    // Écouter les mises à jour du rapport
    newSocket.on('reportUpdated', (data) => {
      console.log('📄 Rapport mis à jour via WebSocket:', data);
    });

    newSocket.on('updateReport', (data) => {
      console.log('🔄 Mise à jour directe du rapport:', data);
    });

    return () => {
      // Cleanup uniquement si c'est toujours la même socket ET le même report
      if (currentReportRef.current === reportId && (socketRef.current === newSocket || isConnectingRef.current)) {
        newSocket.off('reportUsersUpdate', handleReportUsersUpdate);
        newSocket.off('error', handleSocketError);
        console.log(`👋 Déconnexion WebSocket du PV ${reportId} par ${user}`);
        
        // Log de la sortie du rapport (seulement si l'entrée a été loggée)
        if (hasLoggedEntryRef.current) {
          hasLoggedEntryRef.current = false;
          logAction(LOG_ACTIONS.REPORT_LEAVE, formatReportNumber(report.number, report.year), {
            indice: reportData?.indice || null
          });
        }
        
        newSocket.emit('leaveReport', { reportId });
        newSocket.disconnect();
        socketRef.current = null;
        currentReportRef.current = null;
        isConnectingRef.current = false;
        setSocket(null);
        setConnectedUsers([]);
        setTotalConnectedUsers(0);
      }
    };
  }, [report, user, reportData, isRestricted, userHasAccess, fetchUserDisplayName, enqueuePresenceNotification, accessDenied]);

  // Ajout de l'effet pour récupérer les informations de l'utilisateur
  useEffect(() => {
    const fetchUserInfo = async () => {
      try {
        const token = sessionStorage.getItem('authToken');
        const response = await axios.get(`/api/user/matricule/${user}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setIsModerator(response.data.moderator || false);
      } catch (err) {
        console.error('Erreur lors de la récupération des informations utilisateur:', err);
        setIsModerator(false);
      }
    };

    if (user) {
      fetchUserInfo();
    }
  }, [user]);

  const handleBack = () => {
    pickReport(null);
  };

  // Gestionnaires de redimensionnement des colonnes
  const handleMouseDown = (e, side) => {
    setIsDragging(side);
    setDragStartX(e.clientX);
    if (side === 'left') {
      setInitialLeftWidth(leftWidth);
    }
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
  };

  const handleMouseMove = (e) => {
    if (!isDragging) return;

    const deltaX = e.clientX - dragStartX;
    const totalWidth = window.innerWidth;
    const deltaPercent = (deltaX / totalWidth) * 100;

    if (isDragging === 'left') {
      const newLeftWidth = Math.max(10, Math.min(40, initialLeftWidth + deltaPercent));
      // Assurer que la partie centrale garde au moins 20%
      const centralWidth = 100 - newLeftWidth - rightWidth;
      if (centralWidth >= 20) {
        setLeftWidth(newLeftWidth);
      }
    } else if (isDragging === 'right') {
      const newRightWidth = Math.max(10, Math.min(40, rightWidth - deltaPercent));
      // Assurer que la partie centrale garde au moins 20%
      const centralWidth = 100 - leftWidth - newRightWidth;
      if (centralWidth >= 20) {
        setRightWidth(newRightWidth);
      }
    }
  };

  const handleMouseUp = () => {
    setIsDragging(null);
    setDragStartX(0);
    document.body.style.cursor = '';
    document.body.style.userSelect = '';
  };

  // Ajouter les écouteurs d'événements au montage du composant
  useEffect(() => {
    if (isDragging) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
    } else {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    }

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isDragging, dragStartX, initialLeftWidth, leftWidth, rightWidth]);

  useEffect(() => {
    if (!report) {
      setReportData(null);
      setAccessDenied(false);
      setAccessDeniedMessage('');
      setError(null);
      setLoading(false);
      return;
    }

    setAccessDenied(false);
    setAccessDeniedMessage('');
    setError(null);

    const fetchReportData = async () => {
      try {
        const token = sessionStorage.getItem('authToken');
        const response = await axios.get(`/api/report/search`, {
          params: { number: report.number, year: report.year },
          headers: { Authorization: `Bearer ${token}` },
        });
        setReportData(response.data);
        setLoading(false);
      } catch (err) {
        if (err.response?.status === 403) {
          setAccessDenied(true);
          setAccessDeniedMessage('Accès refusé à ce PV.');
          setReportData({
            number: report.number,
            year: report.year,
            indice: report.indice || '',
            access: { type: 'restricted', users: [] }
          });
        } else if (err.response?.status === 404) {
          setError('PV non trouvé.');
        } else {
          setError('Erreur lors de la récupération des données du PV.');
        }
        setLoading(false);
      }
    };

    setLoading(true);
    fetchReportData();
  }, [report, pickReport]);

  useEffect(() => {
    if (!reportData || !user || accessDenied) return;

    const reportId = `${reportData.number}-${reportData.year}-${reportData.indice || 'none'}`;
    
    if (accessUpdateRef.current[reportId]) return;

    const isRestricted = reportData.access?.type === 'restricted';
    const userHasAccess = isRestricted
      ? Array.isArray(reportData.access?.users) && (reportData.access.users.includes(user) || isModerator)
      : true;

    if (userHasAccess) {
      const updateUserAccess = async () => {
        try {
          accessUpdateRef.current[reportId] = true;
          
          const token = sessionStorage.getItem('authToken');
          console.log(`Enregistrement de l'accès pour ${reportData.number}/${reportData.year}`);
          
          await axios.post(
            `/api/user/${user}/accessed-reports`,
            {
              report: {
                number: reportData.number,
                year: reportData.year,
                indice: reportData.indice,
                createdBy: reportData.createdBy,
                createdAt: reportData.createdAt,
                reportType: reportData.reportType,
                unit: reportData.unit,
                restricted: reportData.access.type === 'restricted',
                lastAccessed: new Date().toISOString(),
                archived: false,
              },
            },
            { headers: { Authorization: `Bearer ${token}` } }
          );
        } catch (err) {
          console.error("Erreur lors de l'enregistrement de l'accès au PV:", err);
          accessUpdateRef.current[reportId] = false;
        }
      };
      
      updateUserAccess();
    }
  }, [reportData, user, isModerator, accessDenied]);

  useEffect(() => {
    return () => {
      if (report) {
        const reportId = `${report.number}-${report.year}-${report.indice || 'none'}`;
        if (accessUpdateRef.current[reportId]) {
          delete accessUpdateRef.current[reportId];
        }
      }
    };
  }, [report]);

  const renderAccessDenied = (message) => {
    const displayNumber = reportData?.number || report?.number || '__';
    const displayYear = reportData?.year || report?.year || '__';
    const displayIndice = reportData?.indice || report?.indice || '__';

    return (
      <Box 
        sx={{ 
          height: '92vh', 
          display: 'flex', 
          flexDirection: 'column', 
          justifyContent: 'center', 
          alignItems: 'center',
          bgcolor: 'background.default'
        }}
      >
        <Paper 
          elevation={3} 
          sx={{ 
            p: 4, 
            maxWidth: 600, 
            width: '90%',
            textAlign: 'center',
            borderRadius: 2
          }}
        >
          <Stack spacing={3} alignItems="center">
            <LockIcon 
              sx={{ 
                fontSize: 60, 
                color: 'error.main',
                mb: 2
              }} 
            />
            
            <Typography 
              variant="h4" 
              component="h1" 
              gutterBottom
              color="error"
              fontWeight="bold"
            >
              Accès refusé
            </Typography>

            <Typography 
              variant="body1" 
              color="text.secondary"
              sx={{ mb: 2 }}
            >
              {message || "Vous ne possédez pas les droits d'accès pour ce procès-verbal."}{' '}
              <Typography component="span" fontWeight="bold">
                <SettingsFieldDisplay fieldName="shortJudicialDistrict" />.{displayIndice || '__'}.
                <SettingsFieldDisplay fieldName="judicialDivision" />.{displayNumber}/{displayYear}
              </Typography>
            </Typography>

            <Button
              variant="contained"
              color="primary"
              startIcon={<ArrowBackIcon />}
              onClick={handleBack}
              sx={{ 
                mt: 2,
                borderRadius: 2,
                textTransform: 'none',
                px: 4
              }}
            >
              Retour
            </Button>
          </Stack>
        </Paper>
      </Box>
    );
  };

  if (!report) {
    return (
      <Box 
        sx={{ 
          height: '92vh', 
          display: 'flex', 
          flexDirection: 'column', 
          justifyContent: 'center', 
          alignItems: 'center',
          bgcolor: 'background.default'
        }}
      >
        <Paper 
          elevation={3} 
          sx={{ 
            p: 4, 
            maxWidth: 600, 
            width: '90%',
            textAlign: 'center',
            borderRadius: 2
          }}
        >
          <Stack spacing={3} alignItems="center">
            <DescriptionIcon 
              sx={{ 
                fontSize: 60, 
                color: 'warning.main',
                mb: 2
              }} 
            />
            
            <Typography 
              variant="h4" 
              component="h1" 
              gutterBottom
              color="warning.main"
              fontWeight="bold"
            >
              Aucun procès-verbal sélectionné
            </Typography>

            <Typography 
              variant="body1" 
              color="text.secondary"
              sx={{ mb: 2 }}
            >
              Veuillez sélectionner un procès-verbal pour continuer.
            </Typography>

            <Button
              variant="contained"
              color="primary"
              startIcon={<ArrowBackIcon />}
              onClick={handleBack}
              sx={{ 
                mt: 2,
                borderRadius: 2,
                textTransform: 'none',
                px: 4
              }}
            >
              Retour
            </Button>
          </Stack>
        </Paper>
      </Box>
    );
  }

  if (loading) {
    return (
      <Box sx={{ height: '92vh', display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center' }}>
        <p>Chargement...</p>
      </Box>
    );
  }

  if (accessDenied) {
    return renderAccessDenied(accessDeniedMessage);
  }

  if (error || !reportData) {
    return (
      <Box 
        sx={{ 
          height: '92vh', 
          display: 'flex', 
          flexDirection: 'column', 
          justifyContent: 'center', 
          alignItems: 'center',
          bgcolor: 'background.default'
        }}
      >
        <Paper 
          elevation={3} 
          sx={{ 
            p: 4, 
            maxWidth: 600, 
            width: '90%',
            textAlign: 'center',
            borderRadius: 2
          }}
        >
          <Stack spacing={3} alignItems="center">
            <ErrorIcon 
              sx={{ 
                fontSize: 60, 
                color: 'error.main',
                mb: 2
              }} 
            />
            
            <Typography 
              variant="h4" 
              component="h1" 
              gutterBottom
              color="error"
              fontWeight="bold"
            >
              Erreur
            </Typography>

            <Typography 
              variant="body1" 
              color="text.secondary"
              sx={{ mb: 2 }}
            >
              {error || 'Données du PV indisponibles.'}
            </Typography>

            <Button
              variant="contained"
              color="primary"
              startIcon={<ArrowBackIcon />}
              onClick={handleBack}
              sx={{ 
                mt: 2,
                borderRadius: 2,
                textTransform: 'none',
                px: 4
              }}
            >
              Retour
            </Button>
          </Stack>
        </Paper>
      </Box>
    );
  }

  if (isRestricted && !userHasAccess) {
    return renderAccessDenied("Vous ne possédez pas les droits d'accès pour ce procès-verbal.");
  }

  return (
    <>
      <Box sx={{
        height: '93.5vh',
        width: '100vw',
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column'
      }}>
        <Box sx={{
          height: '54px',
          width: '100%',
          backgroundColor: '#e0e0e0',
          flexShrink: 0
        }}>
          <Up
            connectedUsers={connectedUsers}
            totalConnectedUsers={totalConnectedUsers}
          />
        </Box>
        <Box sx={{
          height: 'calc(93.5vh - 54px)',
          width: '100%',
          display: 'flex',
          overflow: 'hidden',
          position: 'relative'
        }}>
          <Box sx={{
            width: `${leftWidth}%`,
            height: '100%',
            backgroundColor: '#f5f5f5',
            overflow: 'hidden',
            position: 'relative'
          }}>
            <Left />
          </Box>

          {/* Poignée de redimensionnement gauche */}
          <Box
            sx={{
              width: '4px',
              height: '100%',
              backgroundColor: 'divider',
              cursor: 'col-resize',
              position: 'relative',
              '&:hover': {
                backgroundColor: 'grey.400'
              },
              '&::after': {
                content: '""',
                position: 'absolute',
                left: '50%',
                top: '50%',
                transform: 'translate(-50%, -50%)',
                width: '2px',
                height: '20px',
                backgroundColor: 'grey.600',
                borderRadius: '1px'
              }
            }}
            onMouseDown={(e) => handleMouseDown(e, 'left')}
          />

          <Box sx={{
            width: `${100 - leftWidth - rightWidth}%`,
            height: '100%',
            backgroundColor: '#ffffff',
            overflow: 'hidden'
          }}>
            <Central />
          </Box>

          {/* Poignée de redimensionnement droite */}
          <Box
            sx={{
              width: '4px',
              height: '100%',
              backgroundColor: 'divider',
              cursor: 'col-resize',
              position: 'relative',
              '&:hover': {
                backgroundColor: 'grey.400'
              },
              '&::after': {
                content: '""',
                position: 'absolute',
                left: '50%',
                top: '50%',
                transform: 'translate(-50%, -50%)',
                width: '2px',
                height: '20px',
                backgroundColor: 'grey.600',
                borderRadius: '1px'
              }
            }}
            onMouseDown={(e) => handleMouseDown(e, 'right')}
          />

          <Box sx={{
            width: `${rightWidth}%`,
            height: '100%',
            backgroundColor: '#f5f5f5',
            overflow: 'hidden'
          }}>
            <Right />
          </Box>
        </Box>
      </Box>
      {presenceNotifications.map((notif, index) => (
        <Snackbar
          key={notif.id}
          open
          autoHideDuration={4000}
          onClose={() => handleClosePresenceNotification(notif.id)}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
          sx={{ bottom: 16 + index * 64 }}
        >
          <Alert
            onClose={() => handleClosePresenceNotification(notif.id)}
            severity={notif.action === 'join' ? 'info' : 'warning'}
            variant="filled"
            sx={{ minWidth: 280 }}
          >
            {`${resolveUserName(notif.userId)} ${
              notif.action === 'join' ? 'a rejoint le document' : 'a quitté le document'
            }`}
          </Alert>
        </Snackbar>
      ))}
    </>
  );
}

export default Core;



